import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { DEFAULT_QUERY_OPTIONS } from '../support/constants';
import { fetchItem } from '../support/queries';
import { RootState } from '../support/store';
import { checkCiscoProductsLoaded } from '../support/slices/productsSlice';
import { CacheKey } from '../types/cache-keys';
import { GtmArchitecture } from '../types/gtm-architecture';
import { useFetchItem, validId } from './base-hooks';

/**
 * Data hooks for the go-to-market architecture pages (`/hybrid-mesh-firewall`,
 * `/universal-ztna`, ...).
 *
 * Two deliberately separate concerns:
 *   - `useGtmArchitecture` — the motion's own content (sections, sales process,
 *     competitive positioning, labs, demos). One request.
 *   - `useGtmArchitectureProducts` — the products the motion sells, read out of the redux
 *     product master the app already loads once per session. A go-to-market motion spans the
 *     features of many products, so products are cached on their own rather than being treated
 *     as part of the motion's content — and a page view costs no product requests at all.
 *
 * Between them the page loads with a single API call.
 */

/**
 * Route slug -> `go_to_market.id`.
 *
 * `go_to_market` has no slug column, so the mapping lives here. Rows are keyed by id
 * rather than name because a row's name is editable copy. Seeded by the backend's
 * `seed_gtm_architecture_content` management command, which prints this same mapping.
 */
const ARCHITECTURE_GTM_IDS: Record<string, number> = {
  'hybrid-mesh-firewall': 10,
  'universal-ztna': 31,
  'security-cloud-control': 55,
  'ai-security': 56,
  identity: 57,
  splunk: 58,
  'identity-segmentation': 59,
  'power-security-ops': 60,
};

/** The slugs that have an architecture page, for route registration. */
const ARCHITECTURE_SLUGS = Object.keys(ARCHITECTURE_GTM_IDS);

const architectureGtmId = (slug: string): number | undefined => ARCHITECTURE_GTM_IDS[slug];

/**
 * The motion's content. `detailed` hydrates every nested `go_to_market_*` collection.
 *
 * `feature_refs` trims nested feature lists to id / name / product: the page resolves features
 * against the product master, and the long-form descriptions the payload would otherwise repeat
 * under every component and use case are about half its size. `depth` is pinned so the page's
 * shape doesn't ride on the API's default — use cases nest components, which nest features.
 */
const GTM_ARCHITECTURE_PARAMS = { detailed: true, depth: 3, feature_refs: true };

const useGtmArchitecture = (slug: string, options?: object) => {
  const id = architectureGtmId(slug);

  return useFetchItem<GtmArchitecture>(
    'db/go-to-market',
    id,
    // The 'page' segment keeps this trimmed payload out of the admin editor's cache entry, which
    // hits the same record without `feature_refs` and needs the full feature rows. The admin's
    // writes still reach this key, since they invalidate on the shared prefix.
    () => [CacheKey.GtmSolution, CacheKey.GtmArchitecture, 'page'],
    GTM_ARCHITECTURE_PARAMS,
    { ...options, enabled: validId(id) }
  );
};

const EMPTY_PRODUCT_MAP: Record<number, any> = {};

/**
 * The Cisco product master, from redux.
 *
 * `App` loads it once per session, so the page reads what's already there instead of fetching
 * the catalogue again per view. The dispatch is a no-op when it's loaded, and covers a direct
 * landing on an architecture URL.
 */
const useCiscoProductMaster = () => {
  const dispatch = useDispatch();
  const productMap = useSelector(
    (state: RootState) => state.products?.ciscoProductMap ?? EMPTY_PRODUCT_MAP
  );
  const isLoading = useSelector((state: RootState) => !!state.products?.isCiscoProductsLoading);
  const error = useSelector((state: RootState) => state.products?.ciscoProductsError);

  useEffect(() => {
    dispatch(checkCiscoProductsLoaded() as any);
  }, [dispatch]);

  return { productMap, isLoading, isError: !!error };
};

/**
 * Which licenses unlock which of the given features.
 *
 * The master is a list response, so its features carry no licenses — that mapping only exists on
 * a feature's own row. It's read only when someone clicks a license chip to filter, so it's
 * fetched then, for that product's features alone: a handful of small requests, each cached per
 * feature and shared with the feature modal. (The product's own detail carries the same mapping
 * but weighs 1-2 MB, which is not a filter click's worth of data.)
 */
const useFeatureLicenses = (featureIds: number[], options?: object) => {
  const key = featureIds.slice().sort((a, b) => a - b).join(',');

  return useQuery({
    queryKey: [CacheKey.ProductFeature, 'architecture-licenses', key],
    queryFn: async () => {
      const rows = await Promise.all(
        featureIds.map((fid) =>
          fetchItem(`db/product-feature/${fid}`, { detailed: true })
            .then((detail: any) => ({ fid, licenses: detail?.license || [] }))
            .catch(() => ({ fid, licenses: [] as any[] }))
        )
      );

      const index: Record<number, any[]> = {};
      rows.forEach((row) => {
        index[row.fid] = row.licenses;
      });
      return index;
    },
    ...{ ...DEFAULT_QUERY_OPTIONS, ...options, enabled: featureIds.length > 0 },
  });
};

/**
 * One feature's own row, fetched when a feature card is expanded.
 *
 * Licenses and success criteria are the two things the product master doesn't carry, and
 * they're only ever read inside a feature's modal — so they cost one request per opened card
 * rather than three per feature on every page load.
 */
const useProductFeatureDetail = (featureId?: number, options?: object) =>
  useFetchItem<any>(
    'db/product-feature',
    featureId,
    () => [CacheKey.ProductFeature, 'architecture-feature'],
    { detailed: true },
    { ...options, enabled: validId(featureId) }
  );

const refProductId = (ref: any): number | null => {
  const raw = ref?.product ?? ref?.product_id;
  if (raw == null) return null;
  return typeof raw === 'object' ? raw?.id ?? null : raw;
};

/** The motion's components and use cases — the two places products are referenced from. */
const productBearingItems = (gtm?: GtmArchitecture): any[] => [
  ...((gtm as any)?.component || []),
  ...((gtm as any)?.use_case || []).flatMap((useCase: any) => useCase?.components || []),
  ...((gtm as any)?.use_case || []),
];

export interface ArchitectureProduct {
  id: number;
  name: string;
  description: string;
  category: string;
  features: any[];
  specs: any[];
  use_cases: string[];
}

export interface ArchitectureProductData {
  products: ArchitectureProduct[];
  productDetails: Record<number, any>;
  productAppliances: Record<number, any[]>;
  productVersions: Record<number, any[]>;
  featureDetails: Record<number, any>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * The products a motion sells, as product cards.
 *
 * Products are resolved from each component's / use case's `product_feature[]` (falling back to
 * the item's own `product` reference) and enriched from the master. A product absent from the
 * master is skipped — the master is the source of truth for names. Everything a card shows —
 * licenses, appliances, versions, offer type, per-feature sales copy — is already on the master
 * row, so this derives rather than fetches.
 */
const useGtmArchitectureProducts = (gtm?: GtmArchitecture): ArchitectureProductData => {
  const { productMap, isLoading, isError } = useCiscoProductMaster();

  return useMemo(() => {
    const items = productBearingItems(gtm);
    const hasMaster = Object.keys(productMap).length > 0;

    const products: ArchitectureProduct[] = [];
    const seen = new Set<number>();
    const productDetails: Record<number, any> = {};
    const productAppliances: Record<number, any[]> = {};
    const productVersions: Record<number, any[]> = {};
    const featureDetails: Record<number, any> = {};

    const pushProduct = (productId: number | null, item: any) => {
      if (productId == null) return;
      const master = hasMaster ? productMap[productId] : null;
      if (hasMaster && !master) return;

      // The motion's features for this product, gathered across every component and use case
      // that references it — a product usually shows up under more than one.
      const featureRefs = (item?.product_feature || [])
        .filter((f: any) => {
          const pid = refProductId(f);
          return pid != null ? pid === productId : true;
        })
        .map((f: any) => (typeof f === 'object' ? f : { id: f, name: String(f) }));

      if (seen.has(productId)) {
        const existing = products.find((p) => p.id === productId);
        if (existing) {
          const known = new Set(existing.features.map((f: any) => f.id));
          featureRefs.forEach((f: any) => {
            if (!known.has(f.id)) existing.features.push(f);
          });
        }
        return;
      }
      seen.add(productId);

      const embedded = typeof item?.product === 'object' ? item.product : null;

      // The master row *is* the product detail this page used to fetch per product: offer type,
      // architectures, licenses, components, features, appliances, versions, sales link.
      if (master) {
        productDetails[productId] = master;
        productAppliances[productId] = master.appliance || [];
        productVersions[productId] = master.version || [];
        (master.feature || []).forEach((feature: any) => {
          if (feature?.id != null) featureDetails[feature.id] = feature;
        });
      }

      products.push({
        id: productId,
        name: master?.name || embedded?.name || `Product ${productId}`,
        description: master?.description || master?.short_description || embedded?.description || '',
        category: String(master?.offer_type || '')
          .replace(/_/g, ' ')
          .toUpperCase()
          .slice(0, 20),
        features: featureRefs,
        specs: master?.specification || [],
        use_cases: (master?.use_case || [])
          .map((useCase: any) => (typeof useCase === 'object' ? useCase?.name : useCase))
          .filter(Boolean),
      });
    };

    items.forEach((item) => {
      let matched = false;
      (item.product_feature || []).forEach((f: any) => {
        const pid = refProductId(f);
        if (pid != null && (!hasMaster || productMap[pid])) {
          matched = true;
          pushProduct(pid, item);
        }
      });
      if (!matched) pushProduct(refProductId(item), item);
    });

    return {
      products,
      productDetails,
      productAppliances,
      productVersions,
      featureDetails,
      isLoading,
      isError,
    };
  }, [gtm, productMap, isLoading, isError]);
};

export {
  ARCHITECTURE_GTM_IDS,
  ARCHITECTURE_SLUGS,
  architectureGtmId,
  useGtmArchitecture,
  useGtmArchitectureProducts,
  useFeatureLicenses,
  useProductFeatureDetail,
};
